export const parsePolishNumber = (value) => {
  const compact = String(value ?? "")
    .replace(/[\s\u00A0]/g, "")
    .replace(/[^0-9,.-]/g, "");
  const commaCount = (compact.match(/,/g) || []).length;
  const dotCount = (compact.match(/\./g) || []).length;
  let normalized = compact;

  if (commaCount > 0 && dotCount > 0) {
    const decimalSeparator = compact.lastIndexOf(",") > compact.lastIndexOf(".") ? "," : ".";
    const groupingSeparator = decimalSeparator === "," ? /\./g : /,/g;
    normalized = compact.replace(groupingSeparator, "").replace(decimalSeparator, ".");
  } else if (commaCount > 1) {
    normalized = compact.replace(/,/g, "");
  } else if (dotCount > 1) {
    normalized = compact.replace(/\./g, "");
  } else {
    normalized = compact.replace(",", ".");
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const paymentForEqualInstallments = (principal, annualRate, months) => {
  if (principal <= 0 || months <= 0) return 0;
  const monthlyRate = annualRate / 1200;
  if (monthlyRate === 0) return principal / months;
  return principal * (monthlyRate * (1 + monthlyRate) ** months) / ((1 + monthlyRate) ** months - 1);
};

export const amortizationRows = (principal, annualRate, months, repaymentType) => {
  if (principal <= 0 || months <= 0) return [];
  const monthlyRate = annualRate / 1200;
  const equalPayment = paymentForEqualInstallments(principal, annualRate, months);
  const fixedCapital = principal / months;
  let balance = principal;
  const rows = [];

  for (let month = 1; month <= months && balance > 0.01; month += 1) {
    const interest = balance * monthlyRate;
    const capital = repaymentType === "decreasing"
      ? Math.min(fixedCapital, balance)
      : Math.min(Math.max(0, equalPayment - interest), balance);
    const installment = capital + interest;
    balance = Math.max(0, balance - capital);
    rows.push({ month, installment, capital, interest, balance });
  }

  return rows;
};

const scheduleSummary = (rows) => ({
  first: rows[0]?.installment || 0,
  last: rows[rows.length - 1]?.installment || 0,
  interest: rows.reduce((sum, row) => sum + row.interest, 0),
});

export const calculateScenarios = ({
  price,
  savings,
  years,
  repaymentType,
  rateType,
  calculationMode,
  singleRate,
  rate10,
  rate20,
  rateRkm,
}) => {
  const months = Math.max(1, Math.round(years * 12));
  const maxRkmOwnPercent = rateType === "fixed" ? 0.3 : 0.2;
  const target10 = price * 0.1;
  const target20 = price * 0.2;
  const maxRkmOwn = Math.min(200000, price * maxRkmOwnPercent);
  const availableRkmOwn = Math.min(Math.max(0, savings), maxRkmOwn);
  const minimumRkmOwn = Math.max(0, target20 - 100000);
  const rkmMissing = Math.max(0, minimumRkmOwn - availableRkmOwn);
  const rkmOwn = Math.min(maxRkmOwn, Math.max(availableRkmOwn, minimumRkmOwn));
  const rkmGuaranteeNeed = Math.max(0, target20 - rkmOwn);
  const rkmGuarantee = Math.min(100000, rkmGuaranteeNeed, Math.max(0, 200000 - rkmOwn));
  const rkmProgramGap = Math.max(0, target20 - rkmOwn - rkmGuarantee);
  const rkmCashOverLimit = savings > 200000;
  const rkmPercentOverLimit = savings > price * maxRkmOwnPercent;
  const rkmGuaranteeUseful = rkmGuarantee > 0;
  const rkmEligible = months >= 180
    && !rkmCashOverLimit
    && !rkmPercentOverLimit
    && rkmMissing === 0
    && rkmProgramGap === 0;
  const rates = calculationMode === "single"
    ? { ten: singleRate, twenty: singleRate, rkm: singleRate }
    : { ten: rate10, twenty: rate20, rkm: rateRkm };

  const rawItems = [
    {
      key: "10",
      title: "Wkład 10%",
      kicker: "niższy próg wejścia",
      own: target10,
      cashUsed: Math.min(Math.max(0, savings), target10),
      missing: Math.max(0, target10 - savings),
      loan: price - target10,
      rate: rates.ten,
    },
    {
      key: "20",
      title: "Wkład 20%",
      kicker: "mniejszy kredyt",
      own: target20,
      cashUsed: Math.min(Math.max(0, savings), target20),
      missing: Math.max(0, target20 - savings),
      loan: price - target20,
      rate: rates.twenty,
    },
    {
      key: "rkm",
      title: "RKM",
      kicker: rkmGuaranteeUseful ? "wariant z gwarancją BGK" : "wariant bez gwarancji BGK",
      own: rkmOwn,
      cashUsed: availableRkmOwn,
      missing: rkmMissing,
      loan: price - rkmOwn,
      rate: rates.rkm,
      guarantee: rkmGuarantee,
      guaranteeFee: rkmGuarantee * 0.01,
      programGap: rkmProgramGap,
      cashOverLimit: rkmCashOverLimit,
      guaranteeUseful: rkmGuaranteeUseful,
      maxOwnPercent: maxRkmOwnPercent * 100,
      eligible: rkmEligible,
      eligibilityReasons: [
        ...(months < 180 ? ["RKM wymaga okresu kredytowania wynoszącego co najmniej 15 lat."] : []),
        ...(rkmCashOverLimit ? ["Wpisane środki własne przekraczają limit 200 000 zł dla pieniężnego wkładu własnego w tym wariancie kalkulatora."] : []),
        ...(rkmPercentOverLimit ? [`Wpisany wkład przekracza ${maxRkmOwnPercent * 100}% ceny przy wybranym rodzaju oprocentowania.`] : []),
        ...(rkmMissing > 0 ? [`Przy tej cenie gwarancja może pokryć najwyżej 100 000 zł. Aby wariant był możliwy, trzeba dołożyć co najmniej ${Math.ceil(rkmMissing).toLocaleString("pl-PL")} zł własnych środków.`] : []),
        ...(rkmProgramGap > 0 ? ["Dla tej ceny suma wkładu i gwarancji musiałaby przekroczyć limit 200 000 zł, dlatego kalkulator wyłącza wariant z gwarancją."] : []),
      ],
    },
  ];

  const items = rawItems.map((item) => {
    const rows = amortizationRows(item.loan, item.rate, months, repaymentType);
    return { ...item, rows, ...scheduleSummary(rows) };
  });

  const recommended = savings >= target20
    ? "20"
    : savings >= target10
      ? "10"
      : rkmEligible
        ? "rkm"
        : null;
  const ten = items[0];
  const twenty = items[1];

  return {
    items,
    months,
    recommended,
    insight: {
      additionalCash: Math.max(0, target20 - target10),
      paymentDifference: Math.abs(ten.first - twenty.first),
      interestDifference: Math.abs(ten.interest - twenty.interest),
      paymentDirection: twenty.first < ten.first ? "niższa" : "wyższa",
      interestDirection: twenty.interest < ten.interest ? "niższe" : "wyższe",
    },
  };
};

export const validateMortgageInput = (values) => {
  const errors = {};
  if (values.price <= 0) errors.price = "Wpisz cenę większą od zera.";
  if (values.savings < 0) errors.savings = "Środki własne nie mogą być ujemne.";
  if (values.years < 1 || values.years > 40) errors.years = "Wpisz okres od 1 do 40 lat.";
  const rateFields = values.calculationMode === "single"
    ? ["singleRate"]
    : ["rate10", "rate20", "rateRkm"];
  rateFields.forEach((field) => {
    if (values[field] <= 0 || values[field] > 30) errors[field] = "Wpisz oprocentowanie od 0,01% do 30%.";
  });
  return errors;
};


// Do not propose a 20% contribution when the user entered 10% or less.
export const visibleMortgageScenarios = (items, price, savings) =>
  items.filter(item => item.key !== "20" || savings > price * 0.1);
